import { Component, enableProdMode, ViewChild, AfterViewInit, NgZone } from '@angular/core';
import { Platform, LoadingController, NavController } from 'ionic-angular';
import { LocalStorageProvider } from '../providers/local-storage';
import { StatusBar } from '@ionic-native/status-bar';
import { SplashScreen } from '@ionic-native/splash-screen';
import { AuthProvider } from '../providers/auth';
import { UserProvider } from '../providers/user';
import { API } from '../providers/api';
import { AppNavProvider } from '../providers/app_nav';
import { OnPremiseProvider, HealthCheckStatus, InspectorStatus } from '../providers/on-premise';
import { LicenseStatus } from '../pages/license-error-page/license-error-page';
import { Subscription } from 'rxjs';
import { InspectorBusyProvider } from '../providers/inspector-busy';
import { ModalProvider } from '../providers/popup/modal';
import { PopoverProvider } from '../providers/popup/popover';
import { TrackableJobProvider } from '../providers/trackable-job';
import { TranslationProvider } from '../providers/translation';
import { TranslateService } from '@ngx-translate/core';
import { SettingsProvider } from '../providers/settings';

enableProdMode();

@Component({
    templateUrl: 'app.html',
    selector: 'app-base'
})
export class BrainBuilder implements AfterViewInit {
    @ViewChild('myNav') nav: NavController;
    public rootPage: any;
    public showLoadingScreen = true;
    public externalServiceError = false;

    public login = true;
    public migrate = false;
    public dashboard = false;

    public isLoggedIn: boolean;
    public isLoggedInSubscription: Subscription;
    public showOnPremiseHeader = false;

    constructor(
        private auth: AuthProvider,
        private userProvider: UserProvider,
        private translationProvider: TranslationProvider,
        private ngZone: NgZone,
        private appNavProvider: AppNavProvider,
        private onPremiseProvider: OnPremiseProvider,
        public api: API,
        public platform: Platform,
        public statusBar: StatusBar,
        public splashScreen: SplashScreen,
        public loadingCtrl: LoadingController,
        private popoverProvider: PopoverProvider,
        private modalProvider: ModalProvider,
        private localStorageProvider: LocalStorageProvider,
        private inspectorBusyProvider: InspectorBusyProvider,
        private trackableJobProvider: TrackableJobProvider,
        private translate: TranslateService,
        private settingsProvider: SettingsProvider
    ) {
        platform
            .ready()
            .then(async () => {
                statusBar.styleDefault();
                splashScreen.hide();

                const health = await this.onPremiseProvider.healthcheck();

                const inspectorStatus = await this.onPremiseProvider.getInspectorStatus();
                this.handleInspectorStatus(inspectorStatus);

                this.showOnPremiseHeader = this.onPremiseProvider.isOnPremiseElectronApp();

                // We should check on authentication last, since it can dismiss the loading spinner.
                if (this.handleOnPremiseHealthcheck(health)) {
                    await this.verifyAuth();
                }

                this.showLoadingScreen = false;
            })
            .then(
                () => {
                    this.initializeAfterLoadingScreen();
                },
                () => {
                    this.initializeAfterLoadingScreen();
                }
            );
    }

    private initializeAfterLoadingScreen() {
        this.initializeBackgroundProcessing();
        this.initializeTranslation();
    }

    /**
     * Set up processing that needs to run in the background of the application
     */
    private initializeBackgroundProcessing() {
        this.onPremiseProvider.subscribeForHealthCheck(data =>
            this.ngZone.run(() => {
                this.handleOnPremiseHealthcheck(data);
            })
        );

        this.onPremiseProvider.subscribeForInspectorStatus(data =>
            this.ngZone.run(() => {
                this.handleInspectorStatus(data);
            })
        );

        this.trackableJobProvider.initializeJobPolling();
    }

    private initializeTranslation() {
        this.userProvider.userLoaded.subscribe(async user => {
            await this.settingsProvider.load();
            this.translationProvider.setLanguageFromSystem();
        });

        // Make sure the user is loaded so we can display in the correct language
        this.userProvider.load();
    }

    private handleOnPremiseHealthcheck({
        brainBank,
        sdkService,
        detectionService,
        cudaUpToDate,
        license
    }: HealthCheckStatus): boolean {
        // if we don't have a license it means that license check was disabled
        // and we do not need to check inside
        if (sdkService && license) {
            if (license.licenseStatus === 'expired') {
                this.goToPage(`LicenseErrorPage`, { ...license, licenseStatus: LicenseStatus.Expired });
                return false;
            }

            if (license.licenseStatus === 'missing') {
                this.goToPage(`LicenseErrorPage`, { ...license, licenseStatus: LicenseStatus.Missing });
                return false;
            }
        }

        const qualifiedDetectionServiceHealth = this.getQualifiedDetectionServiceHealth(detectionService);

        // cudaUpToDate needs to explicitly be false to trigger an error
        if (!brainBank || !sdkService || !qualifiedDetectionServiceHealth || cudaUpToDate === false) {
            this.externalServiceError = true;
            this.goToPage(`ExternalServiceAlertPage`, {
                brainBank,
                sdkService,
                detectionService: qualifiedDetectionServiceHealth,
                cudaUpToDate
            });
            return false;
        } else if (this.isOnAlertPage() || this.isOnLicenseErrorPage()) {
            this.goToPage(`DashboardPage`);
        }

        return true;
    }

    /**
     * Return false to indicate the detection service is unhealthy only if
     * applicable to the current user capabilities
     */
    private getQualifiedDetectionServiceHealth(detectionService: boolean) {
        const notifyDetection = this.isLoggedIn && this.userProvider.hasDetectionBrainsCapability();
        return notifyDetection ? detectionService : true;
    }

    private goToPage(name: string, data?: any): void {
        this.popoverProvider.closeAll();
        this.modalProvider.closeAll();
        this.nav.setRoot(name, data);
    }

    private async handleInspectorStatus({ isBusy }: InspectorStatus): Promise<void> {
        if (isBusy) {
            await this.inspectorBusyProvider.showBusyModal();
        } else {
            await this.inspectorBusyProvider.dismissBusyModal();
        }
    }

    private isOnAlertPage() {
        const active = this.nav.getActive();
        return active && active.id === 'ExternalServiceAlertPage';
    }

    private isOnLicenseErrorPage() {
        const active = this.nav.getActive();
        return active && active.id === 'LicenseErrorPage';
    }

    private verifyAuth() {
        const token = this.api.getUserToken();

        if (token) {
            this.verifyToken(token); // show loading screen until this is decidedly approved.
        } else {
            this.checkAuthentication();
        }
    }

    public ngAfterViewInit(): void {
        this.isLoggedInSubscription = this.auth.isAuthenticatedSubscription.subscribe(isLoggedIn => {
            this.isLoggedIn = isLoggedIn;
        });

        this.appNavProvider.setNavController(this.nav);
    }

    /**
     * Don't touch this unless you absolutely need to
     * This method is responsible for checking the
     * initial authentication since this file is always loaded on page
     * load, regardless of login-status
     * If the user is not logged in, the AuthProvider will send them to the login page
     * If the user is logged in, we first check to see if they are trying to
     * navigate to a specific page (i.e. the Projects page)
     * If they are not, we just direct them to the Dashboard by default
     */
    private async checkAuthentication(): Promise<void> {
        try {
            const isLoggedIn = await this.auth.checkAuthentication();

            if (isLoggedIn) {
                await this.userProvider.load();
                this.showLoadingScreen = false;

                if (!this.nav.getActive() && !window.location.href.includes('#/')) {
                    this.rootPage = 'DashboardPage';
                }
            } else {
                this.showLoadingScreen = false;
                this.rootPage = 'LoginPage';
            }
        } catch (err) {
            throw err;
        }
    }

    private async invalidToken(message: string = 'Sorry, that token is invalid'): Promise<void> {
        await this.modalProvider.presentError(
            this.translate.instant('Invalid Token'),
            message,
            undefined,
            this.translate.instant('OK')
        );

        /**
         * Remove token from url
         * So we don't attempt to revalidate it
         * again if user reloads
         */
        window.history.replaceState(null, 'Brain Builder', '/');
        this.rootPage = 'LoginPage';
    }

    /**
     * Routes users to proper location
     * based upon token type
     */
    private async validToken(tokenObject: any): Promise<void> {
        switch (tokenObject.message.type) {
            case 'invitation':
                if (
                    tokenObject.message.hasAccount &&
                    tokenObject.message.organizationName &&
                    !tokenObject.message.hasOrganizationAccount
                ) {
                    this.nav.push('JoinOrganizationPage', {
                        organizationName: tokenObject.message.organizationName,
                        migrate: tokenObject.message.migrate,
                        email: tokenObject.message.email
                    });
                } else if (tokenObject.message.hasOrganziationAccount || tokenObject.message.hasAccount) {
                    window.history.replaceState(null, 'Brain Builder', '/');
                    this.rootPage = 'LoginPage';
                } else {
                    await this.localStorageProvider.set('email', tokenObject.message.email);
                    await this.localStorageProvider.set('organizationName', tokenObject.message.organizationName);
                    this.rootPage = 'RegisterPage';
                }
                break;
            case 'resetPassword':
                this.rootPage = 'ResetPasswordPage';
                break;
            default:
                break;
        }
    }

    private async verifyToken(token: string): Promise<any> {
        const loading = this.loadingCtrl.create({
            content: `${this.translate.instant('Verifying Token')}...`
        });

        loading.present();

        try {
            const tokenObject = await this.api.validateUserToken(token);

            loading.dismiss();
            this.validToken(tokenObject);
        } catch (err) {
            loading.dismiss();

            let message;
            switch (err.error.code) {
                case 'TOKEN_NOT_FOUND':
                    message = this.translate.instant('The token you provided does not exist.');
                    break;
                case 'TOKEN_EXPIRED':
                    message = this.translate.instant('The token you provided has expired.');
                    break;
                case 'TOKEN_ALREADY_REDEEMED':
                    message = this.translate.instant('The token you provided has already been redeemed.');
                    break;
                case 'USER_ALREADY_IN_ORGANIZATION':
                    message = err.error.message;
                    break;
                default:
                    message = this.translate.instant('An unknown error occurred. Please try again later.');

                    // in the unknown case, we want to log this error to bugsnag
                    throw err;
            }

            this.invalidToken(message);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts